import { CheckmarkOrb } from '@clearkit/icons';
import classnames from 'classnames';
import {
  CKBox,
  CKButton,
  CKButtonProps,
  CKContainerProps,
  CKLayoutHorizontal,
  includeChildrenByDisplayName,
} from 'clearkit';
import { createContext, FC, useContext } from 'react';

type OnboardingCardFooterProps = CKContainerProps & {
  learnMoreLink?: string;
};

type OnboardingCardComposition = {
  Heading: FC<CKContainerProps>;
  CTAButton: FC<CKButtonProps>;
  Description: FC<CKContainerProps>;
  Footer: FC<OnboardingCardFooterProps>;
};

type OnboardingCardProps = CKContainerProps & {
  isCompleted?: boolean;
};

const OnboardingCardContext = createContext<OnboardingCardProps>({});

export const OnboardingCard: FC<OnboardingCardProps> &
  OnboardingCardComposition = ({
  children,
  className,
  isCompleted,
  ...rest
}) => {
  return (
    <OnboardingCardContext.Provider value={{ isCompleted }}>
      <CKBox {...rest} className={classnames(className, 'p-6 space-y-6')}>
        <div>
          {includeChildrenByDisplayName({
            children,
            componentDisplayName: 'OnboardingCard.Heading',
          })}
          {includeChildrenByDisplayName({
            children,
            componentDisplayName: 'OnboardingCard.Description',
          })}
        </div>
        {includeChildrenByDisplayName({
          children,
          componentDisplayName: 'OnboardingCard.Footer',
        })}
      </CKBox>
    </OnboardingCardContext.Provider>
  );
};

const CTAButton = ({ children, ...rest }: CKButtonProps) => {
  const { isCompleted } = useContext(OnboardingCardContext);

  return isCompleted ? (
    <CKButton {...rest} isDisabled leftIcon={<CheckmarkOrb />}>
      Completed
    </CKButton>
  ) : (
    <CKButton {...rest} variant="simple" variantColor="blue">
      {children}
    </CKButton>
  );
};
OnboardingCard.CTAButton = CTAButton;
OnboardingCard.CTAButton.displayName = 'OnboardingCard.CTAButton';

const Description = ({ children, className, ...rest }: CKContainerProps) => {
  const { isCompleted } = useContext(OnboardingCardContext);

  return (
    <div
      {...rest}
      className={classnames(className, 'text-sm', {
        'text-gray-500': isCompleted,
        'text-gray-800': !isCompleted,
      })}
    >
      {children}
    </div>
  );
};
OnboardingCard.Description = Description;
OnboardingCard.Description.displayName = 'OnboardingCard.Description';

const Heading = ({ children, className, ...rest }: CKContainerProps) => {
  const { isCompleted } = useContext(OnboardingCardContext);
  return (
    <h2
      {...rest}
      className={classnames(className, 'font-medium text-lg mb-1', {
        'text-gray-500': isCompleted,
      })}
    >
      {children}
    </h2>
  );
};
OnboardingCard.Heading = Heading;
OnboardingCard.Heading.displayName = 'OnboardingCard.Heading';

OnboardingCard.Footer = ({ children, learnMoreLink, ...rest }) => (
  <CKLayoutHorizontal {...rest} as="footer" gap="1.5rem" justifyContent="start">
    {children}
    {learnMoreLink ? (
      <a className="ck-link" href={learnMoreLink}>
        Learn more
      </a>
    ) : null}
  </CKLayoutHorizontal>
);
OnboardingCard.Footer.displayName = 'OnboardingCard.Footer';
